import React, {useEffect, useRef, useState} from "react";
import {Link, useLocation, useParams} from "react-router-dom";
import {Alert, Box, Button, CardHeader, Chip, CircularProgress, Stack, Typography} from "@mui/material";
import {ArrowBack, Refresh} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {ContentContainer} from "../components/common/content-container";
import {useUser} from "../providers/user-context";
import {courierName} from "../data-tables-distributor/orders/courier-api";
import {canManageCouriers, updateCourier} from "../courier/courier-api";
import CourierManagementLayout from "../courier/courier-management-layout";
import CourierProfileForm, {availabilityLabel, profileValues, validCapacity} from "../courier/courier-profile-form";
import useCourierData from "../courier/use-courier-data";
import CourierOrders from "../courier/courier-orders";

function ProfileEditor({profile, onSaved, onFailed, onCancel}) {
    const {t, i18n} = useTranslation();
    const [values, setValues] = useState(() => profileValues(profile));
    const [saving, setSaving] = useState(false);
    const pending = useRef(false);
    const mounted = useRef(false);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    // BUSY is calculated by the server. Omit untouched fields to preserve concurrent configuration changes.
    const changes = {};
    if (Number(values.maxActiveOrders) !== profile.maxActiveOrders) changes.maxActiveOrders = Number(values.maxActiveOrders);
    if (values.active !== profile.active) changes.active = values.active;

    const submit = async event => {
        event.preventDefault();
        if (pending.current || !validCapacity(values.maxActiveOrders) || !Object.keys(changes).length) return;
        pending.current = true; setSaving(true);
        try {
            await updateCourier(profile.id, changes, i18n.language);
            if (mounted.current) onSaved();
        } catch (failure) {
            if (mounted.current) onFailed(failure);
        } finally {
            pending.current = false;
            if (mounted.current) setSaving(false);
        }
    };

    return <Box component="form" onSubmit={submit} sx={{maxWidth: 480, mt: 3}}>
        <CourierProfileForm values={values} setValues={setValues} profile={profile} disabled={saving}/>
        <Button type="submit" variant="contained" sx={{mt: 2}} disabled={saving || !validCapacity(values.maxActiveOrders)
            || !Object.keys(changes).length}>{t(saving ? "SavingCourierProfile" : "Save")}</Button>
        <Button sx={{mt: 2, ml: 1}} disabled={saving} onClick={onCancel}>{t("Cancel")}</Button>
    </Box>;
}

function CourierDetails({id}) {
    const {t} = useTranslation();
    const {tenantId, userRole} = useUser();
    const {state} = useLocation();
    const {data: profile, loading, refreshing, error, refresh, version} = useCourierData(tenantId, id);
    const [notice, setNotice] = useState(state?.courierCreated ? "CourierProfileCreated" : "");
    const [saveError, setSaveError] = useState("");
    const [editing, setEditing] = useState(false);

    const failed = failure => {
        setNotice(""); setSaveError(failure.response?.data?.message || "CourierProfileSaveFailed");
        if (failure.response?.status === 409 || !failure.response) { setEditing(false); refresh(); }
    };
    return <>
        <Button component={Link} to="/distributor/users/couriers/profiles" startIcon={<ArrowBack/>}>{t("CourierProfiles")}</Button>
        <CardHeader title={t("CourierProfile")} subheader={profile ? courierName(profile) : ""}
                    action={<Button variant="outlined" startIcon={<Refresh/>} disabled={loading || refreshing || editing}
                        onClick={refresh}>{t("Refresh")}</Button>}/>
        {notice && <Alert severity="success" sx={{mb: 2}}>{t(notice)}</Alert>}
        {saveError && <Alert severity="error" sx={{mb: 2}}>{t(saveError)}</Alert>}
        {loading ? <CircularProgress aria-label={t("LoadingCouriers")}/> : error ?
            <Alert severity="error" action={<Button color="inherit" onClick={refresh}>{t("Retry")}</Button>}>{t(error)}</Alert> :
            <ContentContainer sx={{p: 3}}>
                <Typography variant="h6">{courierName(profile)}</Typography>
                <Typography color="text.secondary" sx={{mb: 2}}>{profile.userEmail}</Typography>
                <Stack direction="row" spacing={1} sx={{mb: 2}}>
                    <Chip label={t(availabilityLabel(profile.availability))}/>
                    <Chip variant="outlined" label={t(profile.active ? "Active" : "Inactive")}/>
                </Stack>
                <Typography>{t("CourierActiveOrders")}: {profile.activeOrderCount} / {profile.maxActiveOrders}</Typography>
                <Typography>{t("CourierRemainingCapacity")}: {profile.remainingCapacity}</Typography>
                {refreshing && <CircularProgress size={20} aria-label={t("LoadingCouriers")}/>}
                <CourierOrders id={id} version={version} onRefresh={refresh} refreshDisabled={editing || refreshing}/>
                {canManageCouriers(userRole) && (!editing ? <Button variant="outlined" sx={{mt: 3}} disabled={refreshing}
                    onClick={() => { setEditing(true); setNotice(""); setSaveError(""); }}>{t("EditCourierProfile")}</Button> : <>
                    <ProfileEditor profile={profile} onFailed={failed} onCancel={() => setEditing(false)}
                        onSaved={() => { setEditing(false); setSaveError(""); setNotice("CourierProfileUpdated"); refresh(); }}/>
                </>)}
            </ContentContainer>}
    </>;
}

export default function CourierProfilePage(props) {
    const {id} = useParams();
    return <CourierManagementLayout {...props} activeTab="profiles"><CourierDetails key={id} id={id}/></CourierManagementLayout>;
}
